import type { CityConfig } from "../city-template/types";

export const reddingConfig: CityConfig = {
  city: "Redding",
  state: "California",
  stateAbbr: "CA",
  slug: "redding",
  route: "/redding",
  title: "Emergency Passport Redding, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Redding, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Shasta County and Northern California travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Redding, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Redding and Northern California travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Redding%20California%20cityscape%20at%20golden%20hour%20with%20the%20iconic%20Sundial%20Bridge%20over%20the%20Sacramento%20River%2C%20modern%20architecture%20against%20lush%20green%20hills%2C%20Mount%20Shasta%20visible%20in%20the%20distant%20background%2C%20warm%20sunset%20light%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20picturesque%20Northern%20California%20scenery&width=1800&height=700&seq=redding-hero-001&orientation=landscape",
  heroImageAlt: "Redding California skyline with Sundial Bridge over Sacramento River and Mount Shasta in the background",
  geoPosition: "40.5865;-122.3917",
  geoPlacename: "Redding, California",
  lastModified: "2026-07-21",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco, CA 94105",
    distance: "Located in downtown SF — approximately 3 hours south of Redding via I-5",
  },
  nearbyCitiesZips: [
    { name: "Redding", zips: "96001–96003, 96049, 96099" },
    { name: "Anderson", zips: "96007" },
    { name: "Red Bluff", zips: "96080" },
    { name: "Shasta Lake", zips: "96019, 96079" },
    { name: "Cottonwood", zips: "96022" },
    { name: "Palo Cedro", zips: "96073" },
    { name: "Weaverville", zips: "96093" },
    { name: "Mount Shasta", zips: "96067" },
    { name: "Yreka", zips: "96097" },
    { name: "Chico", zips: "95926–95929" },
    { name: "Oroville", zips: "95965" },
    { name: "Paradise", zips: "95969" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Northern California departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Sacramento", route: "/sacramento" },
    { name: "San Francisco", route: "/san-francisco" },
    { name: "San Jose", route: "/san-jose" },
    { name: "Los Angeles", route: "/los-angeles" },
    { name: "Oakland", route: "/oakland" },
    { name: "Chico", route: "/chico" },
    { name: "Santa Rosa", route: "/santa-rosa" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Redding remotely via secure courier. We do not have a walk-in office in Redding — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Northern California travelers.",
  fpcOfficeAddress: "447 Sutter St, San Francisco, CA 94108",
  fpcOfficeDistance: "Serving all of Shasta County and the North State",
  serviceAreaIntro: "From the Sundial Bridge to the Sacramento River Trail, from the Cascade foothills to the Trinity Alps — Fast Passport Center serves the entire Redding community. We regularly help travelers from Shasta Lake, Anderson, Red Bluff, and Chico get their passports fast.",
  nearbyCitiesIntro: "We serve all Redding neighborhoods and nearby Northern California communities including Anderson, Cottonwood, Palo Cedro, Mount Shasta, Yreka, and Chico. If you are in Shasta, Tehama, or Trinity County, we have you covered.",
  howItWorksIntro: "Most Redding travelers complete everything online and ship documents overnight. No need to drive to Sacramento or wait in line at the regional agency.",
  mistakesIntro: "With Redding Municipal Airport serving regional travelers and the nearest regional passport agency hours away, Redding residents can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Redding and Northern California travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Redding? Here are the most common ones we hear from North State travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent North State international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Robert M.",
      location: "Redding, CA",
      rating: 5,
      text: "I had a fishing trip to Alaska in 4 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Saved the whole trip.",
      service: "Emergency Passport",
    },
    {
      name: "Lisa K.",
      location: "Shasta Lake, CA",
      rating: 5,
      text: "Needed to renew my passport for a family reunion in Mexico. The process was smooth, they reviewed my documents, and I had my passport in 4 days.",
      service: "Passport Renewal",
    },
    {
      name: "James P.",
      location: "Anderson, CA",
      rating: 5,
      text: "Lost my passport a week before my cruise to the Caribbean. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible.",
      service: "Lost Passport",
    },
    {
      name: "Angela T.",
      location: "Cottonwood, CA",
      rating: 5,
      text: "Living in rural Northern California, I thought I'd have to drive to Sacramento. Fast Passport Center handled everything remotely. My passport arrived in 3 days.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "redding-1",
      name: "Redding Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2400 Churn Creek Rd",
      city: "Redding",
      state: "CA",
      zip: "96002",
      phone: "(530) 222-1100",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Redding",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "redding-2",
      name: "Anderson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2740 Gateway Dr",
      city: "Anderson",
      state: "CA",
      zip: "96007",
      phone: "(530) 365-5115",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "7 miles south of Redding",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "redding-3",
      name: "Red Bluff Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "555 Washington St",
      city: "Red Bluff",
      state: "CA",
      zip: "96080",
      phone: "(530) 527-5111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "30 miles south of Redding",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Tehama County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "redding-4",
      name: "Chico Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "141 W 5th St",
      city: "Chico",
      state: "CA",
      zip: "95928",
      phone: "(530) 343-1194",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "40 miles south of Redding",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Butte County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "redding-5",
      name: "Shasta County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1450 Court St",
      city: "Redding",
      state: "CA",
      zip: "96001",
      phone: "(530) 225-5731",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from Redding Courthouse",
      website: "https://www.co.shasta.ca.us/",
      websiteLabel: "Shasta County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
    {
      id: "redding-6",
      name: "Mount Shasta Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "406 N Mount Shasta Blvd",
      city: "Mount Shasta",
      state: "CA",
      zip: "96067",
      phone: "(530) 926-4111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "60 miles north of Redding",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern Shasta County and Siskiyou County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 3+ hours to San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Redding photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at North State acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Redding applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Redding families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Redding travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Redding." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Redding address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Redding before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Redding for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Northern California travelers — without the long drive to the nearest regional agency." },
    { q: "How fast can I renew my passport in Redding?", a: "Fast Passport Center offers fast passport renewal in Redding in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my trip?", a: "If your passport expires before your trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Redding in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Redding?", a: "Same-day passport appointments require a long drive to the nearest regional agency. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Redding residents." },
    { q: "Can I expedite a child passport in Redding?", a: "Yes. Fast Passport Center expedites child passports in Redding for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Redding with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Redding?",
      a: "With Fast Passport Center's emergency courier service, Redding travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to San Francisco for a passport?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Redding?",
      a: "Popular options include the Redding Main Post Office (96002), Shasta County Clerk (96001), Anderson Post Office (96007), and Red Bluff Post Office (96080). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Redding?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in a rural area near Redding?",
      a: "We serve all of Shasta County and the surrounding North State including Shasta Lake, Cottonwood, Palo Cedro, Weaverville, and Mount Shasta. Our overnight shipping labels work from any UPS location, so your rural location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Redding?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Redding?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Redding and Anderson offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Redding?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Redding?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Redding families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Redding", address: "1465 Churn Creek Rd, Redding, CA 96002", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Redding", address: "1365 Hartnell Ave, Redding, CA 96002", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Redding", address: "2525 Bechelli Ln, Redding, CA 96002", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Redding", address: "1340 Churn Creek Rd, Redding, CA 96003", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Redding", address: "1511 Dana Dr, Redding, CA 96003", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Redding", address: "1525 Lake Blvd, Redding, CA 96003", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Chico", address: "2201 Pillsbury Rd, Chico, CA 95926", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Anderson", address: "5000 Rhonda Rd, Anderson, CA 96007", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Redding",
      address: "2055 Barnum Dr, Redding, CA 96002",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Red Bluff",
      address: "675 Main St, Red Bluff, CA 96080",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Chico",
      address: "2410 Fair St, Chico, CA 95928",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Anderson",
      address: "2050 Mallard Ln, Anderson, CA 96007",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Shasta County southern residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "RDM",
      name: "Redding Municipal Airport",
      distance: "8 miles from downtown Redding",
      address: "6751 Woodrum Cir, Redding, CA 96002",
      note: "The primary airport for Redding and the North State. Regional flights with connections to major hubs.",
      flightUrl: "https://www.cityofredding.gov/government/departments/airports/redding-municipal-airport",
      flightLabel: "Redding Municipal Airport",
      featured: true,
    },
    {
      code: "SAC",
      name: "Sacramento International Airport",
      distance: "140 miles south of Redding",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "Major international airport for Northern California. Accessible via I-5.",
      flightUrl: "https://www.sacramento.aero/smf/",
      flightLabel: "Sacramento Airport",
      featured: false,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "210 miles south of Redding",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and beyond.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "Shasta County Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Shasta County",
      url: "https://www.co.shasta.ca.us/index/clerk_index.aspx",
    },
    {
      label: "Shasta County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Shasta County",
      url: "https://www.shasta.courts.ca.gov/",
    },
  ],
};